/**
 * Canvas pointer fallbacks and hit-testing (InteractionHandler owns the primary pointer pipeline).
 *
 * RoutePlotter prototype mixin: methods moved verbatim out of main.js
 * (Phase 1 enabling refactor). Every method runs with `this` bound to the
 * RoutePlotter instance; main.js attaches the group via
 * Object.assign(RoutePlotter.prototype, pointerMixin).
 */
import { INTERACTION } from '../config/constants.js';
import {
  routeRuns,
  waypointPointIndices,
  nearestOnPolyline,
  legIndexForPointIndex,
  legMidpointIndex
} from '../utils/segmentHitTest.js';

export const pointerMixin = {
  
  /* Mouse handlers now managed by InteractionHandler
  handleMouseDown(event) {
    const rect = this.canvas.getBoundingClientRect();
    const x = event.clientX - rect.left;
    const y = event.clientY - rect.top;
    
    // Check if clicking on existing waypoint
    const clickedWaypoint = this.findWaypointAt(x, y);
    
    if (clickedWaypoint) {
      this.selectedWaypoint = clickedWaypoint;
      this.isDragging = true;
      this.hasDragged = false; // Reset drag flag
      // Store canvas offset for smooth dragging
      const wpCanvas = this.imageToCanvas(clickedWaypoint.imgX, clickedWaypoint.imgY);
      this.dragOffset.x = x - wpCanvas.x;
      this.dragOffset.y = y - wpCanvas.y;
      this.canvas.classList.add('dragging');
      
      // Emit selection event (updates editor UI)
      this.eventBus.emit('waypoint:selected', clickedWaypoint);
      this.updateWaypointList(); // Update list to show selection
      event.preventDefault();
    }
  },
  
  handleMouseMove(event) {
    if (this.isDragging && this.selectedWaypoint) {
      const rect = this.canvas.getBoundingClientRect();
      const x = event.clientX - rect.left;
      const y = event.clientY - rect.top;
      
      // Convert canvas position to image coordinates (clamped to bounds)
      const canvasX = x - this.dragOffset.x;
      const canvasY = y - this.dragOffset.y;
      const imgPos = this.canvasToImage(canvasX, canvasY);
      this.selectedWaypoint.imgX = Math.max(0, Math.min(1, imgPos.x));
      this.selectedWaypoint.imgY = Math.max(0, Math.min(1, imgPos.y));
      this.hasDragged = true; // Mark that actual dragging occurred
      
      // Update path immediately for smooth visual feedback during drag
      this.calculatePath();
      // Batch render calls to prevent excessive rendering (60fps → ~2-3fps)
      this.queueRender();
    }
  },
  
  handleMouseUp(event) {
    if (this.isDragging) {
      this.isDragging = false;
      this.canvas.classList.remove('dragging');
      
      // Emit position changed event if waypoint was actually moved
      // This triggers single auto-save instead of 60+ during drag
      if (this.hasDragged && this.selectedWaypoint) {
        this.eventBus.emit('waypoint:position-changed', this.selectedWaypoint);
        this.hasDragged = false;
        this.announce('Waypoint moved');
      }
      
      this.updateWaypointList();
    }
  },
  
  handleCanvasClick(event) {
    // Don't add waypoint if we actually dragged
    if (this.hasDragged) {
      this.hasDragged = false; // Reset for next time
      return;
    }
    
    const rect = this.canvas.getBoundingClientRect();
    const x = event.clientX - rect.left;
    const y = event.clientY - rect.top;
    
    // Check if clicking on existing waypoint for selection
    const clickedWaypoint = this.findWaypointAt(x, y);
    if (clickedWaypoint) {
      this.selectedWaypoint = clickedWaypoint;
      this.updateWaypointList();
      this.updateWaypointEditor();
      return;
    }
    
    // Determine if major or minor waypoint (Shift+Click for minor)
    const isMajor = !event.shiftKey;
    
    // Convert canvas coordinates to normalized image coordinates
    const imgPos = this.canvasToImage(x, y);
    
    // Create waypoint using factory method
    // Waypoint model handles default properties and validation
    const waypoint = isMajor
      ? Waypoint.createMajor(imgPos.x, imgPos.y)
      : Waypoint.createMinor(imgPos.x, imgPos.y);
    
    // Set default label for major waypoints
    if (isMajor) {
      waypoint.label = `Waypoint ${this.waypoints.length + 1}`;
    }
    
    // Inherit properties from previous waypoint if exists
    // This ensures consistent styling across the route
    if (this.waypoints.length > 0) {
      const previousWaypoint = this.waypoints[this.waypoints.length - 1];
      waypoint.copyPropertiesFrom(previousWaypoint);
    }
    
    // Add waypoint to array
    this.waypoints.push(waypoint);
    
    // Emit waypoint added event (triggers path calculation, save, render)
    // Decoupled approach prevents tight coupling to specific update sequence
    this.eventBus.emit('waypoint:added', waypoint);
    
    this.announce(`${isMajor ? 'Major' : 'Minor'} waypoint added`);
    console.log(`Added ${isMajor ? 'major' : 'minor'} waypoint at (${x.toFixed(0)}, ${y.toFixed(0)})`);
  },
  */ // End of mouse/keyboard handlers now managed by InteractionHandler
  
  /**
   * Find waypoint at screen coordinates (from mouse click)
   * 
   * @param {number} screenX - X coordinate in screen space (CSS pixels)
   * @param {number} screenY - Y coordinate in screen space (CSS pixels)
   * @returns {Waypoint|undefined} Waypoint at position, or undefined
     * @param {Object|Set|null} [exclude] Waypoint (or set) the hit-test must skip
   */
  /**
   * Where a waypoint's branch handle sits, in screen coordinates (COMPOSE-04).
   *
   * Up and to the right of the marker, clear of the route itself so the handle
   * never sits on the line the author is trying to click.
   * @param {Object} waypoint
   * @returns {{x: number, y: number, radius: number}}
   */
  waypointBranchHandleAt(waypoint) {
    const centre = this.imageToCanvas(waypoint.imgX, waypoint.imgY);
    const offset = INTERACTION.WAYPOINT_HIT_RADIUS;
    return { x: centre.x + offset, y: centre.y - offset, radius: offset * 0.75 };
  },

  /**
   * True when a screen point is on that waypoint's branch handle.
   * @param {Object} waypoint
   * @param {number} screenX
   * @param {number} screenY
   * @returns {boolean}
   */
  isOnWaypointBranchHandle(waypoint, screenX, screenY) {
    if (!waypoint) return false;
    const handle = this.waypointBranchHandleAt(waypoint);
    const point = this.screenToCanvas(screenX, screenY);
    const zoom = this.viewport?.zoom || 1;
    return Math.hypot(handle.x - point.x, handle.y - point.y) <= handle.radius / zoom;
  },

  /**
   * The waypoint whose branch handle is under a screen point, or null.
   *
   * Its own hit target, checked ahead of the waypoint itself: the handle sits
   * clear of the marker precisely so it does not steal the marker's clicks,
   * which also puts it outside the marker's hit radius — so a cascade that
   * only looked at handles *after* a waypoint hit would never reach it.
   *
   * @param {number} screenX
   * @param {number} screenY
   * @param {Set<string>} handleWaypointIds Waypoints that carry a handle
   * @returns {Object|null}
   */
  findBranchHandleAt(screenX, screenY, handleWaypointIds) {
    if (!handleWaypointIds || handleWaypointIds.size === 0) return null;
    for (const waypoint of this.waypoints) {
      if (!waypoint.isMajor || !handleWaypointIds.has(waypoint.id)) continue;
      if (this.isOnWaypointBranchHandle(waypoint, screenX, screenY)) return waypoint;
    }
    return null;
  },

  findWaypointAt(screenX, screenY, exclude = null) {
    // Convert screen coords to canvas coords (inverse viewport transform)
    const click = this.screenToCanvas(screenX, screenY);
    
    // Zoom-aware hit radius: constant in screen space, shrinks in canvas space when zoomed
    const zoom = this.viewport?.zoom || 1;
    const threshold = INTERACTION.WAYPOINT_HIT_RADIUS / zoom;
    
    // Find the closest waypoint within the threshold (not first-match)
    let closest = null;
    let closestDist = Infinity;

    for (const wp of this.waypoints) {
      // A drop hit-test runs while the dragged waypoint sits under the
      // cursor, so it would always find itself (ROUTE-01c).
      if (exclude && (wp === exclude || (exclude.has && exclude.has(wp)))) continue;
      const wpCanvas = this.imageToCanvas(wp.imgX, wp.imgY);
      const dx = wpCanvas.x - click.x;
      const dy = wpCanvas.y - click.y;
      const dist = Math.sqrt(dx * dx + dy * dy);
      if (dist <= threshold && dist < closestDist) {
        closest = wp;
        closestDist = dist;
      }
    }

    return closest;
  },

  /**
   * Find the route leg at screen coordinates (Phase 4 canvas affordances).
   *
   * A leg is the path span between consecutive waypoints of one run; the
   * waypoint it leaves owns it, exactly as the inspector's Leg card names
   * it. A branched route's trunk and branches are each hit by their own
   * geometry (DEF-64): the nearest line wins, and the trunk keeps a tie, as
   * it is drawn over its branches. A branch's first leg leaves its fork, so
   * the fork owns it too. Also reports whether the position is on the
   * leg's midpoint "+" insert handle. Edit mode only — legs are not
   * clickable during preview.
   *
   * @param {number} screenX - X in screen space (CSS pixels)
   * @param {number} screenY - Y in screen space (CSS pixels)
   * @returns {{waypoint: Waypoint, waypointIndex: number, next: Waypoint,
   *            branchId: string|null, canInsert: boolean, onPlus: boolean,
   *            midImg: {x: number, y: number}}|null} The leg from `waypoint`
   *          (at `waypointIndex` in the route's array) to `next`, on the
   *          trunk (`branchId` null) or that branch
   */
  findSegmentAt(screenX, screenY) {
    if (this.previewMode) return null;
    if (!this.pathPoints || this.pathPoints.length < 2 || this.waypoints.length < 2) return null;

    const click = this.screenToCanvas(screenX, screenY);
    // Hit radii are constant in screen space (same rule as findWaypointAt)
    const zoom = this.viewport?.zoom || 1;

    const runs = routeRuns({
      waypoints: this.waypoints,
      pathPoints: this.pathPoints,
      progressValues: this.getWaypointProgressValues(),
      branchPaths: this.branchPaths
    });
    // A branch's path can outlive its waypoints: a load that fails late puts
    // the route back, not the paths built for the project it refused (DEF-51)
    const inRoute = new Set(this.waypoints);
    let closest = null;
    for (const run of runs) {
      // A run whose progress does not pair with its waypoints cannot say
      // which leg a point is on, and one whose waypoints are not all the
      // route's is not part of it
      const { waypoints, pathPoints, progressValues } = run;
      if (!pathPoints || pathPoints.length < 2 || waypoints.length < 2) continue;
      if (!progressValues || progressValues.length !== waypoints.length) continue;
      if (!waypoints.every(each => inRoute.has(each))) continue;

      const canvasPath = pathPoints.map(p => this.imageToCanvas(p.x, p.y));
      const nearest = nearestOnPolyline(canvasPath, click.x, click.y);
      if (nearest && (!closest || nearest.dist < closest.nearest.dist)) closest = { run, canvasPath, nearest };
    }
    if (!closest) return null;

    const { run, canvasPath, nearest } = closest;
    const wpIndices = waypointPointIndices(run.progressValues, canvasPath.length);
    const legIndex = legIndexForPointIndex(nearest.index, wpIndices);
    const midIdx = legMidpointIndex(wpIndices, legIndex);
    const mid = canvasPath[midIdx];
    const midDist = Math.hypot(click.x - mid.x, click.y - mid.y);

    // Only the trunk offers the "+". The leg insert gives its waypoint no
    // branch, so on a branch leg it would land a trunk waypoint inside the
    // branch's run and split it (DEF-22).
    const canInsert = run.branchId === null;
    // The "+" handle is a fatter target than the line itself
    const onPlus = canInsert && midDist <= INTERACTION.LEG_PLUS_HIT_RADIUS / zoom;
    if (!onPlus && nearest.dist > INTERACTION.SEGMENT_HIT_RADIUS / zoom) return null;

    const waypoint = run.waypoints[legIndex];
    return {
      waypoint,
      // The owner's index in the route's array, not its place in the run:
      // `waypoint:insert-on-leg` inserts right after it, which for a trunk
      // leg is the trunk's next place even where a branch's run follows
      waypointIndex: this.waypoints.indexOf(waypoint),
      next: run.waypoints[legIndex + 1],
      branchId: run.branchId,
      canInsert,
      onPlus,
      midImg: { x: run.pathPoints[midIdx].x, y: run.pathPoints[midIdx].y }
    };
  }
};
